import React, { forwardRef } from 'react';
import { Table } from 'antd';
import './DataTable.css';
import type { GetRef, TableProps } from 'antd';
import type { TablePaginationConfig } from 'antd/es/table/interface';

const ROOT_CLASS = 'table-card-rows ui-data-table';
const ROW_CLASS_BASE = 'ui-data-table-row';

const DEFAULT_PAGINATION: TablePaginationConfig = {
  showSizeChanger: true,
  showTotal: (total: number) => `Всего: ${total}`,
  pageSizeOptions: ['10', '20', '50', '100'],
};

/** Высота тела таблицы: viewport минус шапка, сайдбар и блок фильтров (подстройка при смене layout). */
const DEFAULT_TABLE_SCROLL_Y = 'calc(100vh - 300px)';

function mergeScroll<RecordType extends object>(
  scroll: TableProps<RecordType>['scroll']
): TableProps<RecordType>['scroll'] {
  if ((scroll as unknown) === false) {
    return scroll as TableProps<RecordType>['scroll'];
  }
  if (scroll == null) {
    return { x: 'max-content', y: DEFAULT_TABLE_SCROLL_Y };
  }
  const s = scroll as Record<string, unknown> & { x?: unknown; y?: unknown };
  const hasY = s.y !== undefined && s.y !== null;
  return {
    ...s,
    x: s.x ?? 'max-content',
    y: hasY ? s.y : DEFAULT_TABLE_SCROLL_Y,
  } as TableProps<RecordType>['scroll'];
}

function mergeRootClassName(user: TableProps['rootClassName']): string | undefined {
  if (user == null || user === '') return ROOT_CLASS;
  return `${ROOT_CLASS} ${user}`.trim();
}

function mergeRowClassName<RecordType extends object>(
  user: TableProps<RecordType>['rowClassName'],
  record: RecordType,
  index: number,
  indent: number
): string {
  if (typeof user === 'function') {
    const extra = user(record, index, indent);
    return [ROW_CLASS_BASE, extra].filter(Boolean).join(' ');
  }
  if (user) {
    return `${ROW_CLASS_BASE} ${user}`.trim();
  }
  return ROW_CLASS_BASE;
}

function mergePagination<RecordType extends object>(
  pagination: TableProps<RecordType>['pagination']
): TableProps<RecordType>['pagination'] {
  if (pagination === false) return false;
  if (pagination == null) {
    return { ...DEFAULT_PAGINATION };
  }
  return { ...DEFAULT_PAGINATION, ...pagination };
}

export type DataTableProps<RecordType extends object = Record<string, unknown>> = TableProps<RecordType>;

export type DataTableRef = GetRef<typeof Table>;

/**
 * Обёртка над Ant Design Table: дефолтная пагинация, базовые классы строк и обёртки
 * (отступы и «карточные» строки — `table-card-rows` в globals.css).
 * Акцент первой колонки: `className: 'primary-cell'` у column.
 */
const DataTableInner = <RecordType extends object>(
  props: TableProps<RecordType>,
  ref: React.Ref<DataTableRef>
) => {
  const { pagination, rowClassName, rootClassName, tableLayout, scroll, ...rest } = props;

  return (
    <div className="table-container">
      <Table<RecordType>
        ref={ref}
        rootClassName={mergeRootClassName(rootClassName)}
        rowClassName={(record, index, indent) => mergeRowClassName(rowClassName, record, index, indent)}
        pagination={mergePagination(pagination)}
        tableLayout={tableLayout ?? 'auto'}
        scroll={mergeScroll(scroll)}
        {...rest}
      />
    </div>
  );
};

export const DataTable = forwardRef(DataTableInner) as <RecordType extends object = Record<string, unknown>>(
  props: TableProps<RecordType> & React.RefAttributes<DataTableRef>
) => React.ReactElement;

export default DataTable;
